// Ionic & Angular Imports
import {Component} from '@angular/core';
import {FormBuilder, Validators, FormGroup} from '@angular/forms';
import {Events, AlertController, PopoverController, Popover, ViewController, 
        NavParams, LoadingController, Loading} from 'ionic-angular';

// Service Imports
import {rsReportSvc} from '../../../../../services/report.service';
import {rsInspectionSvc, Inspection} from '../../../../../services/inspection.service';
import {InspectionType} from '../../../../../services/inspectiontype.service';
import {JurisdictionType} from '../../../../../services/jurisdictiontype.service';

// Component Imports
import {BasePage} from '../../../../base.component';
import {InspectionPopover} from '../inspection-popover/inspection-popover.component';
import {JurisdictionPopover} from '../inspection-popover/jurisdiction-popover.component';

@Component({
    templateUrl: 'inspection-modal.html',
    providers: [
        rsInspectionSvc
    ]
})
export class InspectionModal extends BasePage
{
    // Class member variables
    private modalForm      : FormGroup;
    private editMode       : boolean;
    private initInspection : Inspection;
    private inspectionList : InspectionType[];
    private jurisdictionList : JurisdictionType[];

    constructor( 
        protected alertCtrl : AlertController,
        private navParams : NavParams, 
        private reportSvc: rsReportSvc, 
        private inspectionSvc: rsInspectionSvc,
        private loadingCtrl : LoadingController,
        private viewCtrl : ViewController,
        private formBuilder : FormBuilder,
        private popupCtrl : PopoverController,
        private eventSystem : Events
    ){
        // Construct base class
        super( alertCtrl );

        // Retrieve modal data from NavParams           
        this.editMode = this.navParams.get("editmode");
        this.inspectionList = this.navParams.get("inspectionTypes");
        this.jurisdictionList = this.navParams.get("jurisdictionTypes");
        if( this.editMode ) { 
            this.initInspection = <Inspection>this.navParams.get("inspection");
        } else {
            // Initialize our activity object
            this.initInspection = <Inspection>{
                id: 0,
                inspection : {
                    id: 0,
                    name: "",
                    description:  ""
                },
                jurisdiction : {
                    id: 0,
                    name: "",
                    description: ""
                },
                inspectorname : "",
                passed: false,
                notes: ""
            };
        }

        // Build our form model structure
        this.buildFormModel();
    }

    // Class method for builidng our form model structure 
    private buildFormModel() {
        let inspectionName : string = "";
        let jurisdictionName : string = "";
        if( this.initInspection.inspection.id > 0 ) {
            inspectionName = this.initInspection.inspection.name;
        }
        if( this.initInspection.jurisdiction.id > 0 ) {
            jurisdictionName = this.initInspection.jurisdiction.name;
        }
        this.modalForm = this.formBuilder.group({
            inspection:         [inspectionName, Validators.required],
            inspectiontypeid:   [this.initInspection.inspection.id, Validators.required],
            jurisdiction:       [jurisdictionName, Validators.required ],
            jurisdictiontypeid: [this.initInspection.jurisdiction.id, Validators.required],
            inspectorname:      [this.initInspection.inspectorname, Validators.required],
            passed:             [this.initInspection.passed],
            notes:              [this.initInspection.notes]
        });
    }

    // Class helper method for setting a field in the form to a specified value
    private setFormField( fieldName: string, fieldValue: any ) {
        this.modalForm.controls[fieldName].setValue( fieldValue );
        this.modalForm.controls[fieldName].markAsDirty();
    }

    public selectInspectionType(event) {
        // Create our popover instance
        let popover : Popover = this.popupCtrl.create( InspectionPopover, {
            inspectionTypes: this.inspectionList
        }, {
            cssClass: "customPopover"
        });
        
        // Handle popover callback
        popover.onWillDismiss( (data) => {
            // If there was a selection made
            if( data !== null && data.selected !== undefined ) {
                let inspection : InspectionType = data.selected;
                this.setFormField( 'inspectiontypeid', inspection.id );
                this.setFormField( 'inspection', inspection.name );
            }
        });    

        // Present our popover component 
        popover.present({
            ev: event
        });    
    }    

    public selectJurisdictionType(event) {
        // Create our popover instance
        let popover : Popover = this.popupCtrl.create( JurisdictionPopover, {
            jurisdictionTypes: this.jurisdictionList
        }, {
            cssClass: "customPopover"
        });
        
        // Handle popover callback
        popover.onWillDismiss( (data) => {
            // If there was a selection made
            if( data !== null && data.selected !== undefined ) {
                let jurisdiction : JurisdictionType = data.selected;
                this.setFormField( 'jurisdictiontypeid', jurisdiction.id );
                this.setFormField( 'jurisdiction', jurisdiction.name );
            }
        });    

        // Present our popover component 
        popover.present({
            ev: event
        });                
    }

    // Class helper method to allow the check box to toggle when the label is pressed
    public togglePassed() {
        // Set hte form field to the opposite of the current value
        this.setFormField( 'passed', <boolean>!this.modalForm.controls['passed'].value)
    }        

    // Class method to close the modal view 
    public modalClose() {
        this.viewCtrl.dismiss();
    }

    public modalReset() {
        let inspectionName : string = "";
        let jurisdictionName : string = "";
        if( this.initInspection.inspection.id > 0 ) {
            inspectionName = this.initInspection.inspection.name;
        }
        if( this.initInspection.jurisdiction.id > 0 ) {
            jurisdictionName = this.initInspection.jurisdiction.name;
        }
        this.modalForm.reset( {
            inspection:                 inspectionName,
            inspectiontypeid:           this.initInspection.inspection.id,
            jurisdiction:               jurisdictionName,
            jurisdictiontypeid:         this.initInspection.jurisdiction.id,  
            inspectorname:              this.initInspection.inspectorname,
            passed:                     this.initInspection.passed,
            notes:                      this.initInspection.notes              
        });
    }

    public modalSubmit( formData : any, isValid : boolean ) {
        // Display a loader while the modal submits our changes
        let loader : Loading = this.loadingCtrl.create({
            content: this.editMode ? 'Updating...' : 'Adding ...',
            dismissOnPageChange: true
        });
        loader.present().then( () => {
            // Determine what the submit button should be doing based on the validity
            // of the form and the mode of the modal.
            if( isValid === true && !this.editMode) {
                // The submit button should save a new subcontractor
                this.inspectionSvc.addInspection( formData.inspectiontypeid, formData.jurisdictiontypeid, formData.inspectorname, formData.passed, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('inspection:added', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error adding this inspection to the report.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                });
            } else if( isValid === true && this.editMode ) {
                // The submit button should save changes to an existing subcontractor
                this.inspectionSvc.updateInspection( this.initInspection.id, formData.inspectiontypeid, formData.jurisdictiontypeid, formData.inspectorname, formData.passed, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('inspection:updated', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error updating this inspection.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                }); 
            }
        });
    }

    public modalDelete() {
        // Verify that we are in the correct editMode and that our id is valid
        if( this.editMode && this.initInspection.id > 0 )
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Inspection', 'Are you sure you want to delete "' + this.initInspection.inspection.name + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.inspectionSvc.deleteInspection( this.initInspection.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('inspection:deleted', this.initInspection );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this inspection.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        this.viewCtrl.dismiss().then( () => {
                            loader.dismiss();
                        });
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }    
}    


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/inspection/inspection-modal/inspection-modal.component.ts